import {clsx, type ClassValue} from "clsx";
import {twMerge} from "tailwind-merge";
import {FlowIssue} from "@/types/node";
import {FlowNodeData, FlowNodeTypes} from "@/types/node";
import {Node, Edge} from "@xyflow/react";
import {getNodeTitle} from "@/pages/flow/NodeTypes/FlowNode";
import {getSettingValueByKey, isEmptySettingValue} from "@/utils/index";
import {FlowNodeSetting} from "@/types/node";
import {TransferCallAction} from "@/components/settings/Actions/SelectActionsTable";
import {CustomAction} from "@/api/services/customAction";
import {BookingAction} from "@/components/settings/Actions/SelectActionsTable";
import {validateAssistantFSM} from "@/api/services/queries";
import {ValidateAssistantFSMBody} from "@/api/services/validateAssistantFSMBody";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function remoteValidateFsmCollectIssues({
  modelId,
  fsmData,
  nodes,
}: {
  modelId: string;
  fsmData: unknown;
  nodes: Node<FlowNodeData>[];
}): Promise<FlowIssue[]> {
  try {
    const res = await validateAssistantFSM(modelId, {
      ...(fsmData as unknown as ValidateAssistantFSMBody),
    });

    const isValid = (res as any)?.valid;
    if (isValid) return [];

    const serverIssues: FlowIssue[] = [];
    const errors = (res as any)?.errors || {};

    // Helper function to process error arrays
    const processErrors = (
      errorArray: string[],
      keyPrefix: string,
    ): FlowIssue[] => {
      return errorArray.map((msg: string, idx: number) => {
        const match = msg.match(/state '([^']+)'/i);
        let nodeId: string | null = null;
        let nodeType: FlowNodeTypes = FlowNodeTypes.START;
        let nodeTitle = "";

        if (match && match[1]) {
          const id = match[1];
          const node = nodes.find(n => n.id === id) as
            | Node<FlowNodeData>
            | undefined;
          if (node) {
            nodeId = id as unknown as string;
            nodeType = node.data?.type as FlowNodeTypes;
            nodeTitle = getNodeTitle(node);
          }
        }

        return {
          type: "error",
          message: msg,
          nodeId: nodeId as unknown as string,
          nodeType,
          key: `${keyPrefix}_${nodeId || idx}`,
          nodeTitle,
        };
      });
    };

    // Check schema errors
    if (errors.schema) {
      serverIssues.push(...processErrors(errors.schema, "schema"));
    }

    // Check go errors
    if (errors.go) {
      serverIssues.push(...processErrors(errors.go, "go"));
    }

    return serverIssues;
  } catch (err) {
    console.error("FSM validation error", err);
    return [];
  }
}

export const findFlowDataIssues = (
  nodes: Node[],
  edges: Edge[],
  availableVariables: string[] = [],
  subflowId?: string,
  subflowName?: string,
): FlowIssue[] => {
  const requiredIssues: FlowIssue[] = nodes.flatMap(node => {
    const settings = node.data?.settings as FlowNodeSetting[];
    if (!settings || Object.keys(settings).length === 0) return [];

    return (
      settings.flatMap(setting => {
        const params = setting.params || [];
        const issues = params
          .filter(param => param.required && isEmptySettingValue(param.value))
          .map(param => ({
            type: "warning" as const,
            message:
              param.requiredLabel ||
              `Missing required value for ${param.label}`,
            nodeId: node.id,
            nodeType: node.data?.type as FlowNodeTypes,
            key: param.key!,
            nodeTitle: getNodeTitle(node as Node<FlowNodeData>),
            subflowId,
            subflowName,
          }));
        return issues.length ? issues : [];
      }) || []
    );
  });

  const openEdgesIssues: FlowIssue[] = edges
    .filter(edge => {
      const targetNode = nodes.find(n => n.id === edge.target);
      const sourceNode = nodes.find(n => n.id === edge.source);
      const isTargetPlaceholder =
        targetNode?.type === FlowNodeTypes.PLACEHOLDER &&
        sourceNode?.type !== FlowNodeTypes.JUMP &&
        sourceNode?.type !== FlowNodeTypes.END;
      return Boolean(isTargetPlaceholder);
    })
    .map(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      if (!sourceNode) return null;
      const sourceNodeName = getNodeTitle(sourceNode as Node<FlowNodeData>);
      return {
        type: "error" as const,
        message: "Open edge detected",
        nodeId: sourceNode.id,
        nodeType: sourceNode.data?.type as FlowNodeTypes,
        key: "open_edge",
        nodeTitle: sourceNodeName || sourceNode.data?.title,
        subflowId,
        subflowName,
      } as FlowIssue;
    })
    .filter(Boolean) as FlowIssue[];

  const endNodes = nodes.filter(node => node.type === FlowNodeTypes.END);
  const endNodeIssues =
    endNodes.length === 0
      ? [
          {
            type: "error" as const,
            message: "At least one end node is required",
            nodeId: null,
            nodeType: FlowNodeTypes.END,
            key: "end_node",
            nodeTitle: "End Call",
            subflowId,
            subflowName,
          },
        ]
      : [];

  const endNodeInstructionsIssues: FlowIssue[] = [];
  if (endNodes.length > 0) {
    endNodes.forEach(n => {
      const nodeInstruction = getSettingValueByKey(
        n.data.settings as FlowNodeSetting[],
        "instructions",
      ) as string;

      if (nodeInstruction?.length === 0) {
        endNodeInstructionsIssues.push({
          type: "error",
          message: "End node instructions are required",
          nodeId: n.id,
          nodeType: FlowNodeTypes.END,
          key: "end_node_instructions",
          nodeTitle: getNodeTitle(n as Node<FlowNodeData>),
          subflowId,
          subflowName,
        });
      }
    });
  }

  const speakNodeIssues: FlowIssue[] = [];
  const speakNodes = nodes.filter(n => n.type === FlowNodeTypes.SPEAK);
  if (speakNodes.length > 0) {
    speakNodes.forEach(n => {
      const nodeInstruction = getSettingValueByKey(
        n.data.settings as FlowNodeSetting[],
        "instructions",
      ) as string;
      if (nodeInstruction?.length === 0) {
        speakNodeIssues.push({
          type: "error",
          message: "Speak node instructions are required",
          nodeId: n.id,
          nodeType: FlowNodeTypes.SPEAK,
          key: "speak_node_instructions",
          nodeTitle: getNodeTitle(n as Node<FlowNodeData>),
          subflowId,
          subflowName,
        });
      }
    });
  }

  const CaptureVariablesNodeIssues: FlowIssue[] = [];
  const captureVariablesNodes = nodes.filter(
    n => n.type === FlowNodeTypes.COLLECT_VARIABLES,
  );
  if (captureVariablesNodes.length > 0) {
    captureVariablesNodes.forEach(n => {
      const nodeInstruction = getSettingValueByKey(
        n.data.settings as FlowNodeSetting[],
        "instructions",
      ) as string;

      if (nodeInstruction?.length === 0) {
        CaptureVariablesNodeIssues.push({
          type: "error",
          message: "Capture Variables instructions are required",
          nodeId: n.id,
          nodeType: FlowNodeTypes.COLLECT_VARIABLES,
          key: "capture_variables_node_instructions",
          nodeTitle: getNodeTitle(n as Node<FlowNodeData>),
          subflowId,
          subflowName,
        });
      }
    });
  }

  const jumpToNodeIssues: FlowIssue[] = [];
  const jumpToNodes = nodes.filter(n => n.type === FlowNodeTypes.JUMP);
  if (jumpToNodes.length > 0) {
    jumpToNodes.forEach(n => {
      const jumpToNode = getSettingValueByKey(
        n.data.settings as FlowNodeSetting[],
        "jump_to_step",
      );
      if (jumpToNode?.toString().length === 0) {
        jumpToNodeIssues.push({
          type: "error",
          message: "Jump node jump to step is required",
          nodeId: n.id,
          nodeType: FlowNodeTypes.JUMP,
          key: "jump_node_jump_to_step",
          nodeTitle: getNodeTitle(n as Node<FlowNodeData>),
          subflowId,
          subflowName,
        });
      }
    });
  }

  // Convert availableVariables array to Set for lookups
  const availableVariablesSet = new Set<string>(availableVariables);

  const branchNodeIssues: FlowIssue[] = [];
  const branchNodes = nodes.filter(n => n.type === FlowNodeTypes.BRANCH);
  if (branchNodes.length > 0) {
    branchNodes.forEach(n => {
      const branchNodeRules = getSettingValueByKey(
        n.data.settings as FlowNodeSetting[],
        "condition_type",
      ) as FlowNodeSetting["condition_type"];

      if (
        !branchNodeRules?.deterministic?.rules?.length &&
        !branchNodeRules?.prompt?.description?.length
      ) {
        branchNodeIssues.push({
          type: "error",
          message: "Branch node conditions are required",
          nodeId: n.id,
          nodeType: FlowNodeTypes.BRANCH,
          key: "branch_node_action",
          nodeTitle: getNodeTitle(n as Node<FlowNodeData>),
          subflowId,
          subflowName,
        });
      }

      // Check if variables used in branch conditions exist
      if (branchNodeRules?.deterministic?.rules?.length > 0) {
        branchNodeRules.deterministic.rules.forEach((rule, idx) => {
          const variableName = rule.variable?.name;
          if (variableName && !availableVariablesSet.has(variableName)) {
            branchNodeIssues.push({
              type: "error",
              message: `Variable "${variableName}" no longer exists`,
              nodeId: n.id,
              nodeType: FlowNodeTypes.BRANCH,
              key: `branch_node_missing_variable_${idx}`,
              nodeTitle: getNodeTitle(n as Node<FlowNodeData>),
              subflowId,
              subflowName,
            });
          }
        });
      }
    });
  }

  const collectVariablesNodeIssues: FlowIssue[] = [];
  const collectVariablesNodes = nodes.filter(
    n => n.type === FlowNodeTypes.COLLECT_VARIABLES,
  );
  if (collectVariablesNodes.length > 0) {
    collectVariablesNodes.forEach(n => {
      const variables = getSettingValueByKey(
        n.data.settings as FlowNodeSetting[],
        "variables",
      ) as Array<{key: string; value: string}> | undefined;

      if (!variables || variables.length === 0) {
        collectVariablesNodeIssues.push({
          type: "error",
          message: "Collect variables node must have at least one variable",
          nodeId: n.id,
          nodeType: FlowNodeTypes.COLLECT_VARIABLES,
          key: "collect_variables_node_empty",
          nodeTitle: getNodeTitle(n as Node<FlowNodeData>),
          subflowId,
          subflowName,
        });
      }
    });
  }

  const bookingNodeIssues: FlowIssue[] = [];
  const bookingNodes = nodes.filter(n => n.type === FlowNodeTypes.BOOKING);
  if (bookingNodes.length > 0) {
    bookingNodes.forEach(n => {
      const bookingNodeAction = getSettingValueByKey(
        n.data.settings as FlowNodeSetting[],
        "action",
      ) as BookingAction;
      if (!bookingNodeAction?.action_id) {
        bookingNodeIssues.push({
          type: "error",
          message: "Booking node action is required",
          nodeId: n.id,
          nodeType: FlowNodeTypes.BOOKING,
          key: "booking_node_action",
          nodeTitle: getNodeTitle(n as Node<FlowNodeData>),
          subflowId,
          subflowName,
        });
      }
    });
  }

  const transferNodeIssues: FlowIssue[] = [];
  const transferNodes = nodes.filter(n => n.type === FlowNodeTypes.TRANSFER);
  if (transferNodes.length > 0) {
    transferNodes.forEach(n => {
      const transferNodeAction = getSettingValueByKey(
        n.data.settings as FlowNodeSetting[],
        "action",
      ) as TransferCallAction;

      if (!transferNodeAction?.action_id) {
        transferNodeIssues.push({
          type: "error",
          message: "Transfer node action is required",
          nodeId: n.id,
          nodeType: FlowNodeTypes.TRANSFER,
          key: "transfer_node_action",
          nodeTitle: getNodeTitle(n as Node<FlowNodeData>),
          subflowId,
          subflowName,
        });
      }
    });
  }

  const actionNodeIssues: FlowIssue[] = [];
  const actionNodes = nodes.filter(n => n.type === FlowNodeTypes.ACTION);
  if (actionNodes.length > 0) {
    actionNodes.forEach(n => {
      const actionNodeAction = getSettingValueByKey(
        n.data.settings as FlowNodeSetting[],
        "action",
      ) as CustomAction;

      if (!actionNodeAction?.action_id) {
        actionNodeIssues.push({
          type: "error",
          message: "Action node action is required",
          nodeId: n.id,
          nodeType: FlowNodeTypes.ACTION,
          key: "action_node_action",
          nodeTitle: getNodeTitle(n as Node<FlowNodeData>),
          subflowId,
          subflowName,
        });
      }
    });
  }

  return [
    ...requiredIssues,
    ...openEdgesIssues,
    ...speakNodeIssues,
    ...endNodeIssues,
    ...endNodeInstructionsIssues,
    ...CaptureVariablesNodeIssues,
    ...jumpToNodeIssues,
    ...branchNodeIssues,
    ...collectVariablesNodeIssues,
    ...transferNodeIssues,
    ...bookingNodeIssues,
    ...actionNodeIssues,
  ];
};

/**
 * Checks all subflows (main + subflows) for issues
 * Only uses test nodes to determine issues
 */
export const findAllSubflowsIssues = (
  mainFlow: {flowData: {test: {nodes: Node[]; edges: Edge[]}}},
  subflows: Array<{
    id: string;
    name: string;
    flowData: {test: {nodes: Node[]; edges: Edge[]}};
  }>,
  variables: Array<{name: string}>,
): FlowIssue[] => {
  const allIssues: FlowIssue[] = [];

  // Extract variable names from the variables context
  const allVariables: string[] = variables.map(v => v.name);

  // Check main flow test nodes
  const mainFlowIssues = findFlowDataIssues(
    mainFlow.flowData.test.nodes,
    mainFlow.flowData.test.edges,
    allVariables,
    "main",
    "Main Flow",
  );
  allIssues.push(...mainFlowIssues);

  // Check each subflow's test nodes
  subflows.forEach(subflow => {
    const subflowIssues = findFlowDataIssues(
      subflow.flowData.test.nodes,
      subflow.flowData.test.edges,
      allVariables,
      subflow.id,
      subflow.name,
    );
    allIssues.push(...subflowIssues);
  });

  return allIssues;
};

export const sanitizeVariableName = (value: string): string => {
  if (!value) return "";
  return value
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .replace(/_+/g, "_");
};

export const getVariableTypeLabel = (type: string): string => {
  switch (type) {
    case "string":
      return "String";
    case "integer":
      return "Integer";
    case "int":
      return "Number";
    case "float":
      return "Float";
    case "bool":
    case "boolean":
      return "Boolean";
    case "enum":
      return "Enum";
    case "list":
      return "List";
    default:
      return type;
  }
};
